import { ContentTypesEnum } from "enums";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import DataService from "features/Application/services/dataServices/typedDataService";
import { assessmentFilterTypes } from "features/Library/Assessments/types/state";
import { embeddedVideosFilterTypes } from "features/Library/EmbeddedVideos/types/models";
import { externalLinksFilterTypes } from "features/Library/ExternalLinks/types/models";
import { eventFilterTypes } from "features/Library/Events/types/state";
import { flowFilterTypes } from "features/Library/Flows/types/models";
import { pdfFilterTypes } from "features/Library/Pdfs/types/models";
import { surveyFilterTypes } from "features/Library/Surveys/types/models";
import { videoFilterTypes } from "features/Library/Videos/types/models";
import { formatFilters } from "utils/filterMapUtils";
import { fetchBegin, fetchSuccess } from "../slices/contentAssignmentSlice";
import { type ContentType } from "enums/contentTypes";
import { type PeopleContent } from "../../types";
import { PeopleType } from "../../../Library/PeopleAssignments/types";

const countHeaderName = process.env.REACT_APP_COUNT_HEADER_NAME as string;

const contentMap: { [key in ContentTypesEnum]?: { contentType: ContentType; filterTypes: any } } = {
  [ContentTypesEnum.Uploaded]: { contentType: "video", filterTypes: videoFilterTypes },
  [ContentTypesEnum.Assessments]: { contentType: "assessment", filterTypes: assessmentFilterTypes },
  [ContentTypesEnum.PDFs]: { contentType: "pdf", filterTypes: pdfFilterTypes },
  [ContentTypesEnum.Surveys]: { contentType: "survey", filterTypes: surveyFilterTypes },
  [ContentTypesEnum.Flows]: { contentType: "flow", filterTypes: flowFilterTypes },
  [ContentTypesEnum.AllFlows]: { contentType: "flow", filterTypes: flowFilterTypes },
  [ContentTypesEnum.Scheduled]: { contentType: "flow", filterTypes: flowFilterTypes },
  [ContentTypesEnum.Events]: { contentType: "externalEvent", filterTypes: eventFilterTypes },
  [ContentTypesEnum.Embedded]: { contentType: "embeddedVideo", filterTypes: embeddedVideosFilterTypes },
  [ContentTypesEnum.Links]: { contentType: "link", filterTypes: externalLinksFilterTypes },
};

let abortController = new AbortController();

export const fetchContent =
  (
    entityId: number,
    skip: number,
    top: number,
    sortBy: string,
    sortOrder: string,
    append: boolean,
    peopleType: PeopleType,
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(fetchBegin());

    abortController.abort();
    abortController = new AbortController();
    const signal = abortController.signal;

    try {
      const state = getState().people.contentAssignment;
      const requestedType = state.content.selectedType;
      const { filterTypes, contentType } = contentMap[requestedType]!;
      const { appliedFilter, search } = state.filters;

      const filters = formatFilters(appliedFilter, filterTypes);
      const request = {
        skip,
        top,
        sortBy,
        sortOrder,
        term: search,
        ...filters,
      };

      const apiPeopleType = peopleType === PeopleType.User ? "users" : "groups";

      let response;
      let data: PeopleContent[];

      switch (state.content.selectedType) {
        case ContentTypesEnum.Flows:
        case ContentTypesEnum.AllFlows:
          response = await DataService.people.getFlows(entityId, apiPeopleType, request, signal);
          data = response.data;
          break;

        case ContentTypesEnum.Scheduled:
          response = await DataService.people.getSchedules(entityId, request, signal);
          data = response.data;
          break;

        default:
          response = await DataService.people.getContent(entityId, apiPeopleType, contentType, request, signal);
          data = response.data.map((x) => ({ ...x, ...x.bag }));
          break;
      }

      const count = Number(response.headers[countHeaderName]);

      if (getState().people.contentAssignment.content.selectedType !== requestedType) {
        return;
      }

      dispatch(fetchSuccess({ items: data, totalCount: count, append }));
    } catch (error: any) {
      if (error.code === "ERR_CANCELED") {
        return;
      }
      throw error;
    }
  };
